import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/lib/analytics";
import { Input } from "@/components/ui/input";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import CommChannelsPicker from "@/components/CommChannelsPicker";
import { toast } from "sonner";

export default function Register() {
  const [form, setForm] = useState({ last_name: "", first_name: "", email: "", phone: "", password: "" });
  const [channels, setChannels] = useState([]);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, comm_channels: channels.filter((c) => c.value.trim()), ref: localStorage.getItem("oxo_ref") || null });
      login(data.token, data.user);
      trackEvent("sign_up", { method: "email" });
      toast.success("Cont creat cu succes");
      navigate("/cont");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="max-w-md mx-auto px-6 py-20">
      <div className="border border-slate-200 p-8">
        <span className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Cont nou</span>
        <h1 className="text-3xl font-black tracking-tight mt-2 mb-8">Înregistrare</h1>
        <form onSubmit={submit} className="space-y-5" data-testid="register-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wide font-bold text-slate-500">Nume de familie</label>
              <Input required value={form.last_name} onChange={set("last_name")} className="rounded-none border-slate-300 h-12 mt-2" data-testid="register-last-name" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide font-bold text-slate-500">Prenume</label>
              <Input required value={form.first_name} onChange={set("first_name")} className="rounded-none border-slate-300 h-12 mt-2" data-testid="register-first-name" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide font-bold text-slate-500">Telefon</label>
            <Input required value={form.phone} onChange={set("phone")} className="rounded-none border-slate-300 h-12 mt-2" data-testid="register-phone" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide font-bold text-slate-500">Email</label>
            <Input required type="email" value={form.email} onChange={set("email")} className="rounded-none border-slate-300 h-12 mt-2" data-testid="register-email" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide font-bold text-slate-500">Parolă</label>
            <Input required type="password" minLength={6} value={form.password} onChange={set("password")} className="rounded-none border-slate-300 h-12 mt-2" data-testid="register-password" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide font-bold text-slate-500">Canale de comunicare (opțional)</label>
            <div className="mt-2"><CommChannelsPicker value={channels} onChange={setChannels} /></div>
          </div>
          <div className="flex items-start gap-3 pt-1">
            <input
              type="checkbox"
              id="gdpr-consent"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-1 w-4 h-4 accent-orange-600 shrink-0 cursor-pointer"
              data-testid="register-consent-checkbox"
            />
            <label htmlFor="gdpr-consent" className="text-sm text-slate-600 leading-relaxed cursor-pointer">
              {t("consent.prefix")}{" "}
              <Link to="/termeni-si-conditii" target="_blank" className="text-orange-600 font-semibold hover:underline" data-testid="register-terms-link">{t("legal.terms")}</Link>{" "}
              {t("consent.and")}{" "}
              <Link to="/confidentialitate" target="_blank" className="text-orange-600 font-semibold hover:underline" data-testid="register-privacy-link">{t("legal.privacy")}</Link>.
            </label>
          </div>
          <button disabled={loading || !agreed} className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-60 disabled:cursor-not-allowed text-white px-6 py-4 font-bold uppercase tracking-wide text-sm transition-colors" data-testid="register-submit">
            {loading ? "Se procesează..." : "Creează cont"}
          </button>
        </form>
        <div className="flex items-center gap-3 my-5">
          <span className="h-px bg-slate-200 flex-1" />
          <span className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">sau</span>
          <span className="h-px bg-slate-200 flex-1" />
        </div>
        <GoogleSignInButton label="Înregistrare cu Google" />
        <p className="text-[11px] text-slate-400 mt-3 text-center leading-relaxed" data-testid="register-google-notice">{t("consent.google")}</p>
        <p className="text-sm text-slate-500 mt-6 text-center">
          Ai deja cont? <Link to="/autentificare" className="text-orange-600 font-semibold hover:underline">Autentifică-te</Link>
        </p>
      </div>
    </div>
  );
}
